import { Box } from "@mui/material";
import type { GridColDef, GridRenderCellParams, GridValidRowModel } from "@mui/x-data-grid";
import { FONT_SIZE } from "../../theme/tokens";

/**
 * Shared SystemDataGrid column fragments for the PBJ Model audit + submission grids. Each helper
 * returns a fully-typed `GridColDef<R>` carrying the canonical renderCell / valueFormatter / sizing,
 * so a table composes its columns from these instead of re-declaring identical cell logic. Pass
 * `overrides` to tune per-table sizing/alignment (e.g. a wider `minWidth`, left-aligned numbers)
 * without forking the cell renderer. Styling comes from theme tokens (FONT_SIZE + text.secondary),
 * never hardcoded values.
 */

/**
 * Identifier column rendered monospace + muted (empId / pbjId across the audit + submission grids).
 * Empty ids render as an em dash. Defaults to `minWidth: 120, flex: 1`.
 */
export const monospaceIdColumn = <R extends GridValidRowModel>(field: string, headerName: string, overrides?: Partial<GridColDef<R>>): GridColDef<R> => ({
	field,
	headerName,
	minWidth: 120,
	flex: 1,
	renderCell: (params: GridRenderCellParams<R>) => (
		<Box component="span" sx={{ fontFamily: "monospace", fontSize: FONT_SIZE.SECONDARY, color: "text.secondary" }}>
			{params.value || "—"}
		</Box>
	),
	...overrides,
});

/** Emphasized (bold) name column shared across the audit + submission grids. Defaults to `minWidth: 160, flex: 1`. */
export const boldNameColumn = <R extends GridValidRowModel>(field: string, headerName: string, overrides?: Partial<GridColDef<R>>): GridColDef<R> => ({
	field,
	headerName,
	minWidth: 160,
	flex: 1,
	renderCell: (params: GridRenderCellParams<R>) => <Box sx={{ fontWeight: 600 }}>{params.value}</Box>,
	...overrides,
});

/**
 * Numeric hours column — `type: "number"` with a 2-decimal formatter that renders null/undefined as
 * an empty string (never "NaN"). Defaults to `minWidth: 110`; pass `overrides` for a different width,
 * `flex`, or left-alignment.
 */
export const hoursColumn = <R extends GridValidRowModel>(field: string, headerName: string, overrides?: Partial<GridColDef<R>>): GridColDef<R> => ({
	field,
	headerName,
	minWidth: 110,
	type: "number",
	valueFormatter: (value: number) => (value == null ? "" : value.toFixed(2)),
	...overrides,
});
